import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Upload } from 'lucide-react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import AddPackageModal from '../../../components/shared/AddPackageModal/AddPackageModal';
import ImportPackagesModal from '../../../components/shared/ImportPackagesModal/ImportPackagesModal';
import PackageDetailsPanel from '../../../components/shared/PackageDetailsPanel/PackageDetailsPanel';
import './PackagesPage.css';
import { mockPackages } from '../../../services/mock/MockData';
import cacheStorage from '../../../utils/cacheStorage';

export default function PackagesPage() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchQuery = searchParams.get('search') || '';
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const { t } = useLanguage();

  const filters = ['All', 'Pending', 'Assigned', 'Delivered'];

  const fetchPackages = async () => {
    try {
      setLoading(true);

      // 1. Get the token from cacheStorage
      const token = cacheStorage.getItem('access_token');

      // 2. Attach the token to the headers
      const response = await axios.get('https://smart-cargo-backend-923182991420.us-central1.run.app/api/packages', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setPackages(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch packages:', err);
      setError('Failed to fetch packages from the server.');
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    if (location.state?.newPackage) {
      setPackages(prev => [location.state.newPackage, ...prev]);
      // Clear state to avoid duplicates on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const getStatusDisplay = (status) => {
    const normalized = (status || '')
        .replace(/_/g, ' ')
        .trim()
        .toUpperCase();

    switch (normalized) {
      case 'IN TRANSIT':
        return 'IN TRANSIT';

      case 'ASSIGNED':
        return 'ASSIGNED';

      case 'COMPLETED':
        return 'COMPLETED';

      case 'INCOMING':
        return 'INCOMING';

      case 'DELIVERED':
        return 'DELIVERED';

      case 'PENDING':
        return 'PENDING';

      case 'PICKED UP':
        return 'PICKED UP';

      case 'CANCELLED':
      case 'CANCELED':
        return 'CANCELLED';

      default:
        return normalized;
    }
  };

  const getStatusClass = (status) => {
    const normalized = (status || '')
        .replace(/_/g, ' ')
        .trim()
        .toUpperCase();

    switch (normalized) {
      case 'IN TRANSIT':
        return 'status-in-transit';

      case 'ASSIGNED':
        return 'status-assigned';

      case 'COMPLETED':
      case 'DELIVERED':
        return 'status-completed';

      case 'INCOMING':
      case 'PENDING':
        return 'status-incoming';

      case 'PICKED UP':
        return 'status-picked-up';

      case 'CANCELLED':
      case 'CANCELED':
        return 'status-cancelled';

      default:
        return 'status-unknown';
    }
  };

  const handleSelectPackage = async (id) => {
    try {
      const token = cacheStorage.getItem('access_token'); // Get token

      const response = await axios.get(`https://smart-cargo-backend-923182991420.us-central1.run.app/api/packages/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}` // Attach token
        }
      });

      setSelectedPackage(response.data);
    } catch (err) {
      console.error('Failed to fetch package details:', err);
    }
  };

  useEffect(() => {
    if (searchQuery && packages.length > 0) {
      const match = packages.find(p => p.id === searchQuery);
      if (match) {
        handleSelectPackage(match.id);
      }
    }
  }, [searchQuery, packages]);


  const getFilterStatus = (status) => {
    const normalized = (status || '')
        .replace(/_/g, ' ')
        .trim()
        .toUpperCase();

    switch (normalized) {
      case 'PENDING':
      case 'INCOMING':
        return 'Pending';

      case 'ASSIGNED':
      case 'IN TRANSIT':
      case 'PICKED UP':
        return 'Assigned';

      case 'DELIVERED':
      case 'COMPLETED':
        return 'Delivered';

      default:
        return normalized;
    }
  };

  const filteredPackages = packages.filter(p => {
    const statusMatch =
        activeFilter === 'All' ||
        getFilterStatus(p.status) === activeFilter;

    const query = searchQuery.toLowerCase();

    const searchMatch = !query ||
        (p.tracking_code && p.tracking_code.toLowerCase().includes(query)) ||
        (p.delivery_address?.String && p.delivery_address.String.toLowerCase().includes(query)) ||
        (p.customer_name?.String && p.customer_name.String.toLowerCase().includes(query)) ||
        (p.courier_name?.String && p.courier_name.String.toLowerCase().includes(query));

    return statusMatch && searchMatch;
  });


  return (
      <div className="packages-page">
        <div className="packages-header-container">
          <div className="packages-header-titles">
            <h1 className="packages-title">Packages</h1>
            <p className="packages-subtitle">Track and manage all shipments</p>
          </div>

          <div className="packages-header-actions">
            <button className="btn-import-csv" onClick={() => setIsImportModalOpen(true)}>
              <Upload size={16} />
              Import CSV / Excel
            </button>
            <button className="btn-add-package" onClick={() => setIsAddModalOpen(true)}>
              <Plus size={20} />
              {t?.addPackage || "Add Package"}
            </button>
          </div>
        </div>

        <div className="filter-tabs">
          {filters.map(filter => (
              <button
                  key={filter}
                  className={`filter-tab ${activeFilter === filter ? 'active' : ''}`}
                  onClick={() => setActiveFilter(filter)}
              >
                {filter}
              </button>
          ))}
        </div>

        <div className="packages-table-container">
          {error && <div className="error-message">{error}</div>}
          {loading ? (
              <div className="loading-message">Loading packages...</div>
          ) : (
              <table className="packages-table">
                <thead>
                <tr>
                  <th>ID</th>
                  <th>Delivery Address</th>
                  <th>Customer Name</th>
                  <th>Courier Name</th>
                  <th>Start Date</th>
                  <th>Status</th>
                </tr>
                </thead>
                <tbody>
                {filteredPackages.map(pkg => (
                    <tr key={pkg.id} onClick={() => handleSelectPackage(pkg.id)} style={{ cursor: "pointer" }}>
                      <td className="col-id">{pkg.id}</td>
                      <td className="col-destination">{pkg.delivery_address?.String || '-'}</td>
                      <td className="col-recipient">{pkg.customer_name?.String || '-'}</td>
                      <td className="col-courier">{pkg.courier_name?.String || 'Unassigned'}</td>
                      <td>{pkg.date_start?.String || '-'}</td>
                      <td className="col-status">
                    <span className={`status-badge ${getStatusClass(pkg.status)}`}>
                      {getStatusDisplay(pkg.status)}
                    </span>
                      </td>
                    </tr>
                ))}
                {filteredPackages.length === 0 && (
                    <tr>
                      <td colSpan="6" className="no-data-message">No packages found for this filter.</td>
                    </tr>
                )}
                </tbody>
              </table>
          )}
        </div>
        <PackageDetailsPanel
            isOpen={!!selectedPackage}
            onClose={() => setSelectedPackage(null)}
            packageData={selectedPackage}
            onEdit={() => {
              setIsAddModalOpen(true);
            }}
        />
        <AddPackageModal
            isOpen={isAddModalOpen}
            onClose={() => {
              setIsAddModalOpen(false);
              if(selectedPackage) setSelectedPackage(null); // optional: clear on close if needed
            }}
            onAddSuccess={() => {
              setIsAddModalOpen(false);
              setSelectedPackage(null);
              fetchPackages();
            }}
            editData={selectedPackage}
        />
        <ImportPackagesModal
            isOpen={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
            onImportSuccess={() => {
              setIsImportModalOpen(false);
              fetchPackages();
            }}
        />
      </div>
  );
}